/* product-display.js — AG1-style subscription PDP.
   Pairs with sections/product-display.liquid.

   SCOPE: this file owns the carets, plan selector, FAQ, sticky bar, and cart
   calls. It does NOT own the gallery itself — that is theme.Product
   (theme.js:7023), which boots off the OUTER [data-section-type="product"]
   wrapper and gives us Flickity, thumbnails, zoom, video, and the AJAX
   add-to-cart path for free. Two nested section scopes, two instances.

   PHASE 1 (done): registration, teardown, caret placement.
   PHASE 2 (this commit): carets, plan selection, FAQ. No cart.
   PHASE 3: cart, gift auto-add, sticky-bar visibility.

   Vanilla, no dependencies, prototype-style to match the rest of theme.js. */

window.theme = window.theme || {};

theme.ProductDisplay = (function () {
  var selectors = {
    config: '[data-pd-config]',
    plans: '[data-pd-plans]',
    plan: '[data-pd-plan]',
    sellingPlanInput: '[data-pd-selling-plan]',
    buyOnce: '[data-pd-buy-once]',
    faqs: '[data-pd-faqs]',
    faq: '[data-pd-faq]',
    sticky: '[data-pd-sticky]',
    stickyAtc: '[data-pd-sticky-atc]',
    stickyScrollTop: '[data-pd-sticky-scrolltop]',
    stickyPlan: '[data-pd-sticky-plan]',
    stickyPrice: '[data-pd-sticky-price]',
    stickyCompare: '[data-pd-sticky-compare]',
    form: '.product-single__form',
    addToCart: '[data-add-to-cart]',
    header: '.site-header',
    caret: '[data-pd-caret]',
    stickyTrigger: '[data-pd-sticky-trigger]',
    mainPhotos: '.product__main-photos',
    mainSlider: '[data-product-photos]',
    mediaCol: '.product-display__media-col'
  };

  var classes = {
    selected: 'is-selected',
    stickyVisible: 'is-visible'
  };

  /* Locale-aware. theme.routes is published by layout/theme.liquid:38 and is
     prefixed on multi-locale storefronts, so never hardcode '/cart/add.js'. */
  function routes() {
    var r = (window.theme && theme.routes) || {};
    return {
      cart: r.cart || '/cart.js',
      cartAdd: r.cartAdd || '/cart/add.js'
    };
  }

  function ProductDisplay(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');

    // Every listener is recorded at bind time so onUnload can remove it with
    // the SAME function reference. Without this, editing a setting in the
    // theme customizer re-runs the constructor and double-binds every handler —
    // which shows up as a single click adding two items to the cart.
    this.listeners = [];
    this.observer = null;

    var config = container.querySelector(selectors.config);
    this.settings = {
      giftVariantId: config ? config.getAttribute('data-pd-gift-variant') : '',
      faqSingleOpen: config ? config.getAttribute('data-pd-faq-single-open') === 'true' : false
    };

    this.cache = {
      stickyTrigger: container.querySelector(selectors.stickyTrigger),
      plans: container.querySelector(selectors.plans),
      planEls: container.querySelectorAll(selectors.plan),
      sellingPlanInput: container.querySelector(selectors.sellingPlanInput),
      buyOnce: container.querySelector(selectors.buyOnce),
      sticky: container.querySelector(selectors.sticky),
      stickyAtc: container.querySelector(selectors.stickyAtc),
      stickyScrollTop: container.querySelector(selectors.stickyScrollTop),
      stickyPlan: container.querySelector(selectors.stickyPlan),
      // Price + compare are mirrored in TWO places now — the left stack (desktop)
      // and inside the CTA button (mobile) — so collect all matches and write
      // every one on plan change. querySelectorAll returns [] when none exist.
      stickyPrices: container.querySelectorAll(selectors.stickyPrice),
      stickyCompares: container.querySelectorAll(selectors.stickyCompare),
      form: container.querySelector(selectors.form),
      addToCart: container.querySelector(selectors.addToCart),
      faqEls: container.querySelectorAll(selectors.faq),
      mediaCol: container.querySelector(selectors.mediaCol)
    };

    /* Read by Phase 3 (main ATC, sticky ATC, gift guard). Shape:
         { key, label, price, compare, sellingPlanId, el } */
    this.selectedPlan = null;

    this.init();
  }

  ProductDisplay.prototype = Object.assign({}, ProductDisplay.prototype, {
    init: function () {
      this.measureHeader();
      this.placeCarets();
      this.neutralizeThumbScroll();

      // Header height changes with the announcement bar and the stuck/unstuck
      // state, so the offset is measured, never hardcoded.
      this.on(window, 'resize', this.debounce(this.measureHeader.bind(this), 150));

      this.initCarets();
      this.initGalleryScrollLock();
      this.initPlans();
      this.initFaqs();
      this.initEditor();
      this.initCart();
      this.initStickyBar();
    },

    /* ------------------------------------------------------------------- cart

       The MAIN Add to Cart needs no code at all: it's a real submit inside
       {% form 'product' %}, and theme.Product wires theme.AjaxProduct to
       .product-single__form (theme.js:7307). That posts new FormData(form) to
       /cart/add.js — hidden selling_plan input included — then fires
       'ajaxProduct:added', which theme.CartDrawer listens for to rebuild AND
       open the drawer (theme.js:4515).

       So all we own here is: the one-time "Buy Once" path, and the gift. */
    initCart: function () {
      /* WHICH CONTROL DID THEY PRESS?

         The gift must attach to a subscription and never to a one-time buy. The
         obvious test — "is the selling_plan input non-empty?" — does NOT work:
         until Skio creates the selling plans, that input is empty for BOTH plan
         cards, so the gift would never fire and would be untestable today.

         The durable signal is intent: the main/sticky ATC means "subscribe"
         (a plan card is always selected), the Buy Once link means "one-time".
         That is true now and stays true after Skio goes live. */
      this.addIntent = null;
      this.giftPending = false;

      // capture:true so intent is recorded BEFORE theme.AjaxProduct's own submit
      // handler runs and fires the request.
      this.on(this.cache.form, 'submit', function () {
        this.addIntent = 'subscription';
      }.bind(this), true);

      this.on(this.cache.buyOnce, 'click', this.onBuyOnce.bind(this));

      // Fired by theme.AjaxProduct after the product lands in the cart, and by
      // our own Buy Once path below.
      this.on(document, 'ajaxProduct:added', this.onProductAdded.bind(this));
    },

    /* One-time purchase. Deliberately NOT a form submit — it has to bypass both
       the selling plan and the gift, so it posts on its own with no selling_plan
       key at all.

       It then fires 'ajaxProduct:added', the SAME event the form path fires,
       rather than a parallel cart:build + cart:open pair. That event is handled
       in one place by theme.CartDrawer, which rebuilds and opens in the right
       order; dispatching the two separately would race, because buildCart() is
       an async fetch and cart:open doesn't wait for it. */
    onBuyOnce: function (evt) {
      var btn = evt.currentTarget;
      if (btn.getAttribute('aria-busy') === 'true') return;

      var variantId = btn.getAttribute('data-pd-variant-id');
      if (!variantId) return;

      btn.setAttribute('aria-busy', 'true');
      this.addIntent = 'onetime';

      var self = this;
      this.cartAdd({ id: variantId, quantity: 1 })
        .then(function () {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        })
        .catch(function (err) {
          console.error('[product-display] Buy Once failed', err);
        })
        .then(function () {
          btn.setAttribute('aria-busy', 'false');
          self.addIntent = null;
        });
    },

    /* Runs after ANY successful add — the form's, or ours. */
    onProductAdded: function () {
      var intent = this.addIntent;
      this.addIntent = null;

      if (intent !== 'subscription') return;
      this.maybeAddGift();
    },

    /* Adds the gift exactly once, and only alongside a subscription.

       Ordering matters and is easy to get wrong: theme.AjaxProduct fires
       'ajaxProduct:added' the instant the PRODUCT add resolves, so the drawer is
       ALREADY OPEN by the time our gift request comes back. If we didn't
       re-render afterwards, the shopper would be staring at a drawer with no
       gift in it. Hence the trailing 'cart:build' (theme.js:1722), which
       rebuilds the line items of the already-open drawer without reopening it. */
    maybeAddGift: function () {
      var giftId = this.settings.giftVariantId;
      if (!giftId) return; // setting left blank -> feature off, cleanly.

      // Guards a double-click on Add to Cart: two adds would otherwise both read
      // the cart before either had written the gift, and add it twice.
      if (this.giftPending) return;
      this.giftPending = true;

      var self = this;

      fetch(routes().cart, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // Never duplicate. Survives reloads and other tabs, because it asks
          // the cart what's actually in it rather than trusting a local flag.
          var present = (cart.items || []).some(function (item) {
            return String(item.variant_id) === String(giftId);
          });
          if (present) return null;

          return self.cartAdd({ id: giftId, quantity: 1 });
        })
        .then(function (added) {
          if (!added) return;
          // Refresh the drawer that theme.CartDrawer already opened.
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch(function (err) {
          // A failed gift must never break the purchase the shopper just made.
          console.error('[product-display] gift auto-add failed', err);
        })
        .then(function () {
          self.giftPending = false;
        });
    },

    cartAdd: function (payload) {
      return fetch(routes().cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) {
          return res.json().then(function (body) {
            throw new Error(body.description || body.message || 'Add to cart failed');
          });
        }
        return res.json();
      });
    },

    /* ------------------------------------------------------------- sticky bar */

    initStickyBar: function () {
      var bar = this.cache.sticky;
      if (!bar || !this.cache.addToCart) return;

      // The `hidden` attribute is the no-JS guard (the bar's button needs JS to
      // do anything). With JS we drop it and drive visibility from CSS instead,
      // because `display:none` can't transition.
      bar.hidden = false;

      this.on(this.cache.stickyAtc, 'click', this.onStickyAtc.bind(this));
      this.on(this.cache.stickyScrollTop, 'click', this.onStickyScrollTop.bind(this));

      if (!('IntersectionObserver' in window)) {
        return; // No observer: bar simply never appears. Page still fully works.
      }

      /* The trigger is the whole BUY CLUSTER — Add to Cart, Buy Once, and the
         subtext under it — not the Add to Cart button alone. Watching the button
         would raise the bar while the Buy Once link and its footnote were still
         on screen and perfectly clickable, which is both redundant and jumpy.
         Watching the cluster means the bar arrives at the exact moment the last
         way to buy leaves the screen.

         The bar deliberately DOES coexist with the pinned media column: it docks
         to the top on desktop, the image is pinned below it, and both stay put
         while the panel scrolls. That is the reference behaviour. */
      this.observer = new IntersectionObserver(this.onTriggerIntersect.bind(this), { threshold: 0 });
      this.observer.observe(this.cache.stickyTrigger || this.cache.addToCart);
    },

    /* Show only once the cluster has scrolled UP and out of view — not while it
       is still below the fold on first paint. isIntersecting alone cannot tell
       those two apart, so test which side it left on: bottom <= 0 is "fully
       above the top of the viewport", and nothing else. */
    onTriggerIntersect: function (entries) {
      var entry = entries[0];
      if (!entry) return;

      var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.cache.sticky.classList.toggle(classes.stickyVisible, scrolledPast);
    },

    /* The sticky ATC deliberately does not re-implement adding — it drives the
       real form, so the selling_plan input, the gift, and the drawer are all the
       one code path.

       form.submit() would be WRONG here: it bypasses submit handlers entirely,
       so theme.AjaxProduct would never intercept and the browser would do a full
       page POST. requestSubmit() fires a real submit event; where it's missing
       (Safari < 16), clicking the actual submit button does the same thing. */
    onStickyAtc: function () {
      var form = this.cache.form;
      if (!form) return;

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(this.cache.addToCart);
      } else if (this.cache.addToCart) {
        this.cache.addToCart.click();
      }
    },

    /* Thumbnail/name in the bar scrolls back to the buy cluster. We don't hide the
       bar ourselves — once the cluster is on screen the IntersectionObserver flips
       it off, so the two never fight. Offset by the live header height (the same
       measurement the desktop dock uses) so the target lands below the header
       rather than under it. */
    onStickyScrollTop: function () {
      var target = this.cache.stickyTrigger || this.cache.addToCart;
      if (!target) return;

      var header = document.querySelector(selectors.header);
      var headerH = header && header.getAttribute('data-sticky') === 'true' ? header.offsetHeight : 0;
      var top = window.pageYOffset + target.getBoundingClientRect().top - headerH - 16;

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
    },

    /* ---------------------------------------------------------------- gallery

       Impulse ships NO arrows on the product slider: theme.Slideshow defaults to
       prevNextButtons:false (theme.js:3244) and theme.Product.initProductSlider
       never overrides it. So the carets are ours, and we drive the theme's own
       Flickity instance rather than standing up a second slider. */

    /* Move the carets inside .product__main-photos so they centre on the image
       rather than on the whole column (image + thumbs). The CSS
       only reveals them once they're in there, which doubles as the no-JS
       fallback — without JS there's no Flickity to drive, and a dead arrow is
       worse than no arrow. */
    placeCarets: function () {
      var mainPhotos = this.container.querySelector(selectors.mainPhotos);
      if (!mainPhotos) return;

      var carets = this.container.querySelectorAll(selectors.caret);
      for (var i = 0; i < carets.length; i++) {
        mainPhotos.appendChild(carets[i]);
      }
    },

    /* THE actual fix for the gallery scroll-jump. Deterministic, unlike the
       pinScroll guard below — which was built on a premise that turned out to be
       false (see initGalleryScrollLock).

       theme.Product.onSlideChange ends EVERY slide change with
       activeThumb.scrollIntoView() (theme.js:7885, plus a deferred copy fired on
       the viewport height transitionend at :7882). `activeThumb` is the active
       thumbnail LINK — a.product__thumb, the element that carries .is-active
       (theme.js:7877). Because our media column is position:sticky and the thumb
       strip sits below the fold, that call scrolls the whole WINDOW down to chase
       a thumbnail — and with the image pinned, that reads as the purchase panel
       scrolling by itself on every caret click, ratcheting further each click.

       The thumbnail's own positioning is ALREADY handled by theme.Slideshow's
       childNavGoTo (theme.js:3481), which sets childNavScroller.scrollLeft
       directly. So this scrollIntoView is redundant; its only observable effect
       is the unwanted window scroll. We neutralise it at the source by shadowing
       scrollIntoView with a no-op on the thumb links themselves. This kills BOTH
       the synchronous and the deferred call (same element), with no dependence on
       event ordering or scroll-behavior timing — which is exactly why pinScroll
       could never hold it. Re-applied on Flickity rebuild (image-set variants
       destroy and rebuild the thumbs), so a fresh thumb set is always covered. */
    neutralizeThumbScroll: function () {
      var noop = function () {};
      var thumbs = this.container.querySelectorAll('.product__thumb');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].scrollIntoView = noop;
      }
    },

    /* Resolved lazily at click time, never cached at init.

       theme.Product builds its slider in its own constructor, and with two
       nested section scopes we cannot guarantee it has run by the time we
       initialise. It also DESTROYS and rebuilds the Flickity instance whenever
       an image-set variant changes (theme.js:7808), so a reference cached at
       init would go stale and silently stop paging. Look it up each time. */
    getFlickity: function () {
      if (!window.Flickity || typeof Flickity.data !== 'function') return null;

      var slider = this.container.querySelector(selectors.mainSlider);
      if (!slider) return null;

      return Flickity.data(slider) || null;
    },

    initCarets: function () {
      var carets = this.container.querySelectorAll(selectors.caret);
      if (!carets.length) return;

      for (var i = 0; i < carets.length; i++) {
        this.on(carets[i], 'click', this.onCaretClick.bind(this));
      }
    },

    onCaretClick: function (evt) {
      var flkty = this.getFlickity();

      // Single-media products never get a Flickity instance (initProductSlider
      // bails early), but the carets aren't rendered in that case either.
      if (!flkty) return;

      var dir = evt.currentTarget.getAttribute('data-pd-caret');

      // wrapAround is true in the theme's args, so these never dead-end.
      if (dir === 'prev') {
        flkty.previous();
      } else {
        flkty.next();
      }
    },

    /* BELT-AND-BRACES scroll guard. The PRIMARY defence against the gallery
       scroll-jump is now neutralizeThumbScroll() above, which removes the cause
       outright. This stays as a cheap fallback for any scroll a future theme
       update might introduce through some other path.

       History worth keeping: this guard used to be the primary fix and it never
       held, because its premise was wrong. It assumed the theme set a GLOBAL
       `scroll-behavior: smooth`, so the window's scrollIntoView would animate
       over later frames and could be caught and re-pinned. But the only
       scroll-behavior:smooth in the theme is scoped to .product__thumbs--scroller
       (theme.css.liquid:12989) — the WINDOW scrolls instantly. And because our
       'change' handler is subscribed after theme.Product's, pinScroll ran AFTER
       the instant scroll had already happened, read the moved position, and
       "restored" to it: a no-op. Neutralising scrollIntoView at the source is
       what actually fixes it.

       Subscribing to the Flickity instance's own 'change' event is what makes
       this cover every source at once — carets, swipe, thumbnail clicks, and
       keyboard. */
    initGalleryScrollLock: function () {
      this.lockedFlickity = null;
      this.galleryLockTimer = null;
      this.onGalleryChange = this.pinScroll.bind(this);

      /* Belt-and-braces. The primary defence is subscribing to Flickity's
         'change' event below, but theme.Product.onSlideChange also fires a
         DEFERRED activeThumb.scrollIntoView() on the viewport's height
         `transitionend` (theme.js:7880-7884). product-display.css zeroes that
         transition so the event never fires — but if a theme update ever
         reinstates it, this catches the deferred scroll too. transitionend
         bubbles, so one delegated listener on the container survives every
         Flickity destroy/rebuild, and this.on() means onUnload removes it. */
      this.on(this.container, 'transitionend', function (evt) {
        if (evt.propertyName !== 'height') return;
        if (!evt.target || !evt.target.classList) return;
        if (evt.target.classList.contains('flickity-viewport')) this.pinScroll();
      }.bind(this));

      // Flickity is built by theme.Product, which — with two nested section
      // scopes — may not have run yet when we initialise. Try now, then retry
      // briefly until the instance exists.
      if (this.bindGalleryLock()) return;

      var self = this;
      var tries = 0;
      this.galleryLockTimer = setInterval(function () {
        if (self.bindGalleryLock() || ++tries > 20) {
          clearInterval(self.galleryLockTimer);
          self.galleryLockTimer = null;
        }
      }, 100);
    },

    bindGalleryLock: function () {
      var flkty = this.getFlickity();
      if (!flkty) return false;
      if (flkty === this.lockedFlickity) return true;

      // theme.Product destroys and rebuilds Flickity on image-set variant
      // changes, so a cached instance can go stale. Drop the old subscription
      // before taking the new one.
      if (this.lockedFlickity && typeof this.lockedFlickity.off === 'function') {
        this.lockedFlickity.off('change', this.onGalleryChange);
      }
      this.lockedFlickity = flkty;
      flkty.on('change', this.onGalleryChange);

      // The rebuild that gave us this fresh instance also rebuilt the thumb
      // links, so re-shadow their scrollIntoView on the new nodes.
      this.neutralizeThumbScroll();
      return true;
    },

    /* Re-assert the scroll position captured at the moment the handler runs.
       With neutralizeThumbScroll in place there is normally nothing to undo, so
       this is a no-op re-write; it only bites if some other path moved the
       window. The write is forced INSTANT — the scrolling element is pinned to
       scroll-behavior:auto for just this one write — so it can't itself animate,
       robust in browsers that don't honour `behavior: 'instant'`. */
    pinScroll: function () {
      var x = window.pageXOffset;
      var y = window.pageYOffset;
      var root = document.scrollingElement || document.documentElement;

      function restore() {
        var prev = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(x, y);
        root.style.scrollBehavior = prev;
      }

      restore();
      window.requestAnimationFrame(restore);
    },

    /* ------------------------------------------------------------------ plans

       NOTE ON SAVE %: the badges are computed in Liquid from the section's price
       settings and are CONSTANT for the life of the page — there is no variant
       picker and no client-side input that can change a price. Recomputing them
       in JS would just be re-deriving a constant, so we don't. The prices/compares
       are read off each card's data attributes and mirrored into the sticky bar,
       which is the only surface that shows the *active* plan's price. */

    initPlans: function () {
      var els = this.cache.planEls;
      if (!els || !els.length) return;

      for (var i = 0; i < els.length; i++) {
        this.on(els[i], 'click', this.onPlanClick.bind(this));
      }

      // Roving-tabindex keyboard model for role="radiogroup".
      this.on(this.cache.plans, 'keydown', this.onPlanKeydown.bind(this));

      // Adopt whatever Liquid marked selected, so a no-JS render and a JS render
      // agree on the initial state.
      var initial = this.container.querySelector(selectors.plan + '.' + classes.selected) || els[0];
      this.selectPlan(initial, { focus: false });
    },

    onPlanClick: function (evt) {
      this.selectPlan(evt.currentTarget, { focus: true });
    },

    onPlanKeydown: function (evt) {
      var els = Array.prototype.slice.call(this.cache.planEls);
      var current = els.indexOf(document.activeElement);
      if (current === -1) return;

      var next = null;

      switch (evt.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          next = els[(current + 1) % els.length];
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          next = els[(current - 1 + els.length) % els.length];
          break;
        case ' ':
        case 'Enter':
          next = els[current];
          break;
        default:
          return;
      }

      // Arrow keys would otherwise scroll the page, and Space would page down.
      evt.preventDefault();
      this.selectPlan(next, { focus: true });
    },

    selectPlan: function (el, opts) {
      if (!el) return;
      opts = opts || {};

      var els = this.cache.planEls;
      for (var i = 0; i < els.length; i++) {
        var isTarget = els[i] === el;
        els[i].classList.toggle(classes.selected, isTarget);
        els[i].setAttribute('aria-checked', isTarget ? 'true' : 'false');
        // Roving tabindex: only the checked radio is in the tab order.
        els[i].setAttribute('tabindex', isTarget ? '0' : '-1');
      }

      if (opts.focus) el.focus();

      this.selectedPlan = {
        key: el.getAttribute('data-pd-plan'),
        label: el.getAttribute('data-pd-plan-label') || '',
        price: el.getAttribute('data-pd-plan-price') || '',
        compare: el.getAttribute('data-pd-plan-compare') || '',
        sellingPlanId: el.getAttribute('data-pd-selling-plan-id') || '',
        el: el
      };

      this.writeSellingPlan(this.selectedPlan.sellingPlanId);
      this.syncSticky();
    },

    /* THE SKIO HOOK. The entire custom plan picker exists to do this one write.

       theme.AjaxProduct posts new FormData(form) to /cart/add.js (theme.js:2799),
       so whatever lands in this input is submitted with no further JS. An EMPTY
       value is what Shopify reads as "one-time purchase".

       TODO(Skio) Phase 4: today every card's data-pd-selling-plan-id is empty,
       because the product has no selling_plan_allocations until Skio creates
       them. Once it does, the Liquid populates those attributes and this line
       starts writing real ids — no change needed here. Verify Skio's "Form ID"
       setting matches the product form's id attribute. */
    writeSellingPlan: function (id) {
      if (!this.cache.sellingPlanInput) return;
      this.cache.sellingPlanInput.value = id || '';
    },

    /* Keep the sticky bar's mirrored copy in step with the selection. The bar is
       still hidden in Phase 2 — Phase 3 only has to toggle its visibility, not
       recompute its contents. */
    syncSticky: function () {
      if (!this.selectedPlan) return;

      var plan = this.selectedPlan;

      if (this.cache.stickyPlan) {
        this.cache.stickyPlan.textContent = plan.label;
      }

      var prices = this.cache.stickyPrices;
      for (var i = 0; i < prices.length; i++) {
        prices[i].textContent = plan.price;
      }

      // The compare price carries a <sup>†</sup> footnote marker, so rebuild it as
      // markup rather than clobbering it with textContent. The values come from
      // Liquid-escaped data attributes, not user input. A plan with no compare-at
      // shouldn't render an empty strikethrough, so hide each mirror in that case.
      var compares = this.cache.stickyCompares;
      for (var j = 0; j < compares.length; j++) {
        compares[j].innerHTML = this.escapeHtml(plan.compare) + '<sup>&dagger;</sup>';
        compares[j].hidden = !plan.compare;
      }
    },

    escapeHtml: function (str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    },

    /* -------------------------------------------------------------------- faq

       Native <details>/<summary>. Expand/collapse, keyboard access, and screen
       reader semantics are all free. Liquid sets `open` on the first item, so
       the first-open state survives a no-JS render — JS never has to set it.
       The only behaviour worth adding is optional single-open (accordion). */
    initFaqs: function () {
      var faqs = this.cache.faqEls;
      if (!faqs || !faqs.length) return;

      // Bind on EVERY FAQ (not just when single-open is on), because the
      // scroll-pin below has to run on every toggle. Single-open is now just one
      // of the things onFaqToggle does.
      this.stickyAnchorTop = null;
      this.stickyAnchorAt = 0;

      for (var i = 0; i < faqs.length; i++) {
        // Snapshot the pinned image's position at the START of the interaction —
        // pointerdown and keydown both fire BEFORE the <details> open-state flips,
        // so we read the pre-toggle geometry. onFaqToggle then reads the
        // post-toggle geometry and cancels the difference.
        var summary = faqs[i].querySelector('summary');
        if (summary) {
          this.on(summary, 'pointerdown', this.captureStickyAnchor.bind(this));
          this.on(summary, 'keydown', this.onFaqSummaryKeydown.bind(this));
        }
        this.on(faqs[i], 'toggle', this.onFaqToggle.bind(this));
      }
    },

    onFaqSummaryKeydown: function (evt) {
      // Only the keys that actually toggle a <summary>.
      if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
        this.captureStickyAnchor();
      }
    },

    /* Where the pinned media column sits in the viewport at the instant the user
       starts a FAQ interaction. Time-stamped so a stray programmatic toggle
       (e.g. the editor opening a block) can't compensate against a stale value. */
    captureStickyAnchor: function () {
      var col = this.cache.mediaCol;
      if (!col) return;
      this.stickyAnchorTop = col.getBoundingClientRect().top;
      this.stickyAnchorAt = Date.now();
    },

    onFaqToggle: function (evt) {
      var opened = evt.currentTarget;

      // Optional single-open accordion. Closing the siblings fires their own
      // toggle events, which re-enter here and re-pin against the same anchor —
      // so the image lands in the same spot no matter how many rows moved.
      if (this.settings.faqSingleOpen && opened.open) {
        var faqs = this.cache.faqEls;
        for (var i = 0; i < faqs.length; i++) {
          if (faqs[i] !== opened) faqs[i].open = false;
        }
      }

      this.repinSticky();
    },

    /* THE FAQ FIX. Expanding or collapsing a <details> reflows the grid that the
       sticky media column lives in. By the time the shopper reaches the FAQ the
       image is near its sticky RELEASE point, so that reflow nudges it — the
       "image jumps on toggle" bug. We cancel the nudge: scroll the window by
       exactly the amount the image moved, so its viewport position is identical
       before and after the toggle. When the image is fully stuck (early FAQs) or
       on mobile (media column not sticky) the delta is 0 and this no-ops. Pairs
       with `overflow-anchor: none` in the CSS, which stops the browser's own
       scroll anchoring from adding a second, competing shift. */
    repinSticky: function () {
      var col = this.cache.mediaCol;
      if (!col || this.stickyAnchorTop == null) return;
      // Only compensate a shift owned by the interaction that set the anchor.
      if (Date.now() - this.stickyAnchorAt > 500) return;

      var delta = col.getBoundingClientRect().top - this.stickyAnchorTop;
      if (!delta) return;

      // Instant, so the compensation itself can never animate.
      var root = document.scrollingElement || document.documentElement;
      var prev = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollBy(0, delta);
      root.style.scrollBehavior = prev;
    },

    /* ---------------------------------------------------------------- editor

       Clicking a block in the customizer should reveal it — otherwise selecting
       an FAQ block scrolls you to a collapsed row and looks broken.

       We deliberately do NOT implement theme.Sections' onBlockSelect hook here,
       because it would never fire. theme.Sections._onBlockSelect (theme.js:750)
       resolves ONE instance via _findInstance(sectionId), which returns the
       first instance with that id. Our two nested scopes share a data-section-id,
       and theme.Product is registered first, so it always wins the lookup — and
       it defines no onBlockSelect, so the event is simply swallowed.

       (The shared id is deliberate and load-bearing: _onSectionUnload matches on
       it to call our onUnload. Giving the inner scope its own id would fix
       onBlockSelect but silently break teardown, which is the worse trade.)

       So we listen to Shopify's event directly and filter by section id. Bound
       through this.on(), so onUnload still removes it. */
    initEditor: function () {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.on(document, 'shopify:block:select', this.onBlockSelected.bind(this));
    },

    onBlockSelected: function (evt) {
      if (!evt.detail || String(evt.detail.sectionId) !== String(this.sectionId)) return;

      var block = evt.target;
      if (!block || !block.hasAttribute) return;

      if (block.hasAttribute('data-pd-faq')) {
        block.open = true;
      }

      var plan = block.closest ? block.closest(selectors.plan) : null;
      if (plan) this.selectPlan(plan, { focus: false });
    },

    /* ----------------------------------------------------------------- utils */

    /* Two offsets, both read off the LIVE header rather than hardcoded, because
       its height moves with the announcement bar and the stuck/unstuck state.

         --pd-sticky-top  the desktop sticky bar docks to the top and must clear
                          the header rather than hide under it.
         --pd-media-top   where the pinned media column comes to rest. It also
                          feeds the --pd-well-size clamp in the CSS, which sizes
                          the image from the leftover viewport height so the
                          thumbnail strip stays on screen while pinned.

       They differ on purpose. The bar is fixed-position and always sits below
       the header's box. The pinned column only has to clear the header if the
       header is ACTUALLY sticky (Header section > Header style > Sticky); when
       it isn't, the header scrolls away, and clearing a header that is no longer
       there would just park the image behind a dead band of whitespace. */
    measureHeader: function () {
      var header = document.querySelector(selectors.header);
      var height = header ? header.offsetHeight : 0;
      var isStuck = header && header.getAttribute('data-sticky') === 'true';

      this.container.style.setProperty('--pd-sticky-top', height + 'px');
      this.container.style.setProperty('--pd-media-top', (isStuck ? height : 0) + 24 + 'px');
    },

    /* Bind + record, so onUnload can unbind. Always use this instead of a bare
       addEventListener. `opts` is passed through (we need capture:true for the
       submit listener) and must be passed to removeEventListener too, or the
       unbind silently no-ops. */
    on: function (el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      this.listeners.push({ el: el, type: type, fn: fn, opts: opts });
    },

    debounce: function (fn, wait) {
      var t;
      return function () {
        var args = arguments;
        var ctx = this;
        clearTimeout(t);
        t = setTimeout(function () {
          fn.apply(ctx, args);
        }, wait);
      };
    },

    /* Called by theme.Sections on shopify:section:unload. */
    onUnload: function () {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.galleryLockTimer) {
        clearInterval(this.galleryLockTimer);
        this.galleryLockTimer = null;
      }
      if (this.lockedFlickity && typeof this.lockedFlickity.off === 'function') {
        this.lockedFlickity.off('change', this.onGalleryChange);
        this.lockedFlickity = null;
      }

      this.listeners.forEach(function (l) {
        l.el.removeEventListener(l.type, l.fn, l.opts);
      });
      this.listeners = [];
    }
  });

  return ProductDisplay;
})();

/* theme.js creates theme.sections inside its own DOMready and then dispatches
   'page:loaded'. This file is deferred, so ordering isn't guaranteed either
   way — handle both. theme.Sections.register also wires the theme-editor
   lifecycle (section load/unload/select), which is what gives us onUnload. */
(function () {
  var registered = false;

  function boot() {
    if (registered) return;
    if (!window.theme || !theme.sections) return;
    registered = true;
    theme.sections.register('product-display', theme.ProductDisplay);
  }

  if (window.theme && theme.sections) {
    boot();
  } else {
    document.addEventListener('page:loaded', boot);
  }
})();
